import { Badge, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { Shipment } from "../../api/types";
import { deliveryTypeLabel, shipmentStatusLabel } from "../../lib/shipmentLabels";

const STATUS_COLORS: Record<string, string> = {
  draft: "gray",
  ready: "blue",
  shipped: "indigo",
  delivered: "green",
};

/**
 * Status + flag badges for a shipment, in order: status, [type], ChirpStack,
 * Accounting, Debt.
 * `compact` (list) keeps badges on one line at xs size; the table scrolls.
 * `showType` renders the delivery-type badge — off in the list (it has its own
 * "Tip livrare" column, and a "Datorie" type would otherwise duplicate the debt badge).
 */
export function ShipmentStatusBadges({
  shipment,
  compact = false,
  showType = true,
}: {
  shipment: Shipment;
  compact?: boolean;
  showType?: boolean;
}) {
  const { t } = useTranslation();
  const size = compact ? "xs" : undefined;
  return (
    <Group gap={4} wrap={compact ? "nowrap" : "wrap"}>
      <Badge variant="light" color={STATUS_COLORS[shipment.status] ?? "gray"} size={size}>
        {shipmentStatusLabel(t, shipment.status)}
      </Badge>
      {showType && shipment.delivery_type_id && shipment.delivery_type_slug !== "normal" && (
        <Badge variant="light" color="cyan" size={size}>
          {deliveryTypeLabel(t, shipment.delivery_type_slug, shipment.delivery_type_name)}
        </Badge>
      )}
      {shipment.chirpstack_added_at && (
        <Badge
          variant="light"
          color="teal"
          size={size}
          title={t("shipments.chirpstackBadge", { ts: new Date(shipment.chirpstack_added_at).toLocaleString() })}
        >
          {t("shipments.chirpstackBadgeShort")}
        </Badge>
      )}
      {shipment.accounting_confirmed_at && (
        <Badge
          variant="light"
          color="violet"
          size={size}
          title={t("shipments.accountingBadge", { ts: new Date(shipment.accounting_confirmed_at).toLocaleString() })}
        >
          {t("shipments.accountingBadgeShort")}
        </Badge>
      )}
      {shipment.on_debt && (
        <Badge variant="light" color="red" size={size}>
          {t("shipments.debtBadge")}
        </Badge>
      )}
    </Group>
  );
}
